import { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import getConfig from "../../config/config";
import { axios } from "../../cores/axiosWrapper";
import useWebSocket from "@hooks/useWebSocket";
import { WEBSOCKET_MESSAGE_TYPES } from "@constants";
import { getWebsocketMessagePending, updateWebsocketMessage } from "@modules/notify";
import { updateAiWorkspaceChatResponse } from "@modules/ai-workspace";
import { snackbarUtils } from "@components_v2/snackbar-provider";
import { useTranslation } from "react-i18next";

export const WebSocketContext = createContext();

const WebSocketProvider = ({ children }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { webSocketTokenIssue } = getConfig.host;
    const [wsToken, setWsToken] = useState("");
    const [wsUrl, setWsUrl] = useState("");
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const loginAttemptRef = useRef(0);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const token = useSelector(({ token }) => token.value);
    const settings = useSelector(({ settings }) => settings.stage);
    const webSocketGatewayUrl = useMemo(() => settings?.General?.websocketEndpoint?.value, [settings]);
    const stage = useSelector(({ stage }) => stage);

    // WebSocket 훅 사용
    const { connectionStatus, lastMessage, error, sendMessage, reconnect, disconnect, isConnected, isConnecting } =
        useWebSocket(wsUrl, wsToken, {
            enabled: isLoggedIn && !!wsToken && !!wsUrl,
            onMessage: (message) => {
                console.log("WebSocket message received:", message);

                // WebSocket 메시지 처리
                if (message.type === "NOTIFY") {
                    dispatch(updateWebsocketMessage(message));
                    console.log("📢 Received NOTIFY message - refreshing message list");
                    console.log("🔄 Automatically fetching messages...");
                    handleSendMessage({
                        action: "fetchMessages",
                        data: { conditions: {} },
                    });
                } else if (message.type === "AI_CAPTION_JOB_STATUS_CHANGED") {
                    if (message.data?.status === "FAILED") {
                        snackbarUtils.error(
                            message.data.error?.message ||
                                t("common::msg::AI Caption task failed.", "AI Caption task failed."),
                        );
                    }
                } else if (message.type === "MESSAGE_LIST") {
                    dispatch(updateWebsocketMessage(message));
                    console.log("📬 Received MESSAGE_LIST - updating message store");

                    if (message.nextToken) {
                        handleSendMessage({
                            action: "fetchMessages",
                            data: { conditions: {}, nextToken: message.nextToken },
                            isFetchMore: true,
                        });
                    }
                } else if (message.type === "SESSION_CHAT_REPLY") {
                    dispatch(updateAiWorkspaceChatResponse(message));
                }
            },
            onOpen: () => {
                console.log("WebSocket connected successfully");
            },
            onClose: () => {
                console.log("WebSocket connection closed");
            },
            onError: (error) => {
                console.error("WebSocket error:", error);
            },
        });

    // 로그인 핸들러
    const handleLogin = useCallback(
        (wsToken) => {
            const attemptNumber = ++loginAttemptRef.current;

            if (isLoggedIn) {
                console.log(`Login attempt #${attemptNumber} cancelled: already logged in`);
                return;
            }

            console.log(`Initiating login attempt #${attemptNumber}:`, {
                url: webSocketGatewayUrl,
                tokenLength: wsToken.length,
            });
            setWsToken(wsToken);
            setWsUrl(webSocketGatewayUrl);
            setIsLoggedIn(true);
        },
        [isLoggedIn, webSocketGatewayUrl],
    );

    // 로그아웃 핸들러
    const handleLogout = useCallback(() => {
        console.log("Logging out...");
        disconnect();
        setWsToken("");
        setWsUrl("");
        setIsLoggedIn(false);
        loginAttemptRef.current = 0;
    }, [disconnect]);

    const issueWebSocketToken = async () => {
        try {
            console.log("Requesting WebSocket token from HTTP endpoint...");

            const response = await axios.post(`${stage.endpoint}/websocket/tokens`);

            if (!response.data) {
                const errorData = await response.json();
                throw new Error(errorData.message || `HTTP ${response.status}`);
            }

            const data = response.data;
            console.log("WebSocket token issued successfully:", {
                token: data.token.substring(0, 20) + "...",
                expiresAt: data.expiresAt,
            });

            return data.token;
        } catch (error) {
            console.error("Failed to issue WebSocket token:", error);
            throw new Error(`토큰 발급 실패: ${error.message}`);
        }
    };

    const onLogin = async () => {
        // 이미 제출 중이거나 연결 중이면 중복 요청 방지
        if (isSubmitting || isConnecting) {
            console.log("Login already in progress, skipping...");
            return;
        }

        if (token) {
            setIsSubmitting(true);
            console.log("Starting login process...");

            try {
                const wsToken = await issueWebSocketToken();

                console.log("Attempting WebSocket connection with issued token...");
                await handleLogin(wsToken);
            } catch (error) {
                console.error("Login process failed:", error);
                setIsSubmitting(false);
            }

            // 연결이 실패한 경우를 위해 타이머로 리셋
            setTimeout(() => {
                if (!isConnecting) {
                    setIsSubmitting(false);
                }
            }, 3000);
        }
    };

    const handleSendMessage = useCallback(
        (data) => {
            if (data.action === "fetchMessages") {
                dispatch(
                    getWebsocketMessagePending({
                        isFetchMore: data.isFetchMore || false,
                    }),
                );
            }
            sendMessage(data);
        },
        [sendMessage, dispatch],
    );

    useEffect(() => {
        if (!webSocketGatewayUrl) return;
        console.log("WebSocketProvider mounted, initiating login...");
        onLogin();
        return () => {
            handleLogout();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [webSocketGatewayUrl]);

    // 브라우저가 닫힐 때 WebSocket disconnect
    useEffect(() => {
        if (!isLoggedIn || !disconnect) return;
        const handleUnload = () => {
            console.log("Browser closing, disconnecting WebSocket...");
            disconnect();
        };
        window.addEventListener("unload", handleUnload);

        return () => {
            window.removeEventListener("unload", handleUnload);
        };
    }, [isLoggedIn, disconnect]);

    const contextValue = {
        sendMessage: handleSendMessage,
        connectionStatus,
        lastMessage,
        error,
        reconnect,
        disconnect,
        isConnected,
        isConnecting,
        isLoggedIn,
    };

    return <WebSocketContext.Provider value={contextValue}>{children}</WebSocketContext.Provider>;
};

export default WebSocketProvider;
